import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, DollarSign, Package, FileText, Users,
  Loader2, Calendar, RefreshCw, Download, ArrowRight, AlertCircle,
  CheckCircle, Clock, BarChart3, PieChart, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell } from "recharts";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#047857', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'];

export default function UnifiedAccounting() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [reportData, setReportData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadReport();
  }, [period]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom" && customStart && customEnd) {
        params.set("start_date", customStart);
        params.set("end_date", customEnd);
      }
      
      const response = await axios.get(`${API}/reports/unified-accounting?${params}`);
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to load report:", error);
      toast.error("Failed to load unified accounting report");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const element = document.getElementById("unified-report");
      const canvas = await html2canvas(element, { scale: 2, backgroundColor: "#ffffff" });
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`unified-accounting-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("Report exported");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="unified-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="text-sm text-slate-500">Loading report...</span>
        </div>
      </div>
    );
  }

  const data = reportData || {};

  return (
    <div className="space-y-8" data-testid="unified-accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-800 tracking-tight flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            Unified Accounting
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Sales, Cash, Receivables & Inventory reconciled
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={loadReport} className="gap-2 rounded-full border-slate-200 text-slate-700 hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            Refresh
          </Button>
          <Button onClick={exportPDF} disabled={exporting} className="gap-2 rounded-full bg-primary hover:bg-primary-700 shadow-primary" data-testid="export-pdf-btn">
            <Download className="w-4 h-4" strokeWidth={1.5} />
            {exporting ? "Exporting..." : "Export PDF"}
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex flex-wrap gap-3 p-4 bg-white rounded-2xl border border-slate-100 shadow-card">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44 rounded-xl border-slate-200" data-testid="period-select">
            <Calendar className="w-4 h-4 mr-2 text-slate-400" strokeWidth={1.5} />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
        {period === "custom" && (
          <>
            <Input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="w-40 rounded-xl border-slate-200"
              data-testid="custom-start-date"
            />
            <Input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="w-40 rounded-xl border-slate-200"
              data-testid="custom-end-date"
            />
            <Button onClick={loadReport} className="rounded-full bg-primary hover:bg-primary-700" data-testid="apply-custom-btn">Apply</Button>
          </>
        )}
      </div>

      <div id="unified-report" className="space-y-6">
        {/* KPI Summary Cards - Premium Design */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Sales */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:shadow-card-hover transition-shadow" data-testid="kpi-sales">
            <div className="flex items-center gap-2 text-emerald-600 text-sm mb-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="font-medium">Total Sales</span>
            </div>
            <p className="text-2xl font-heading font-bold text-emerald-700 font-mono">
              {formatCurrency(data.sales?.total_revenue)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              {data.sales?.invoice_count || 0} invoices + {data.sales?.quick_sale_count || 0} quick sales
            </p>
          </div>

          {/* Cash Collected */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:shadow-card-hover transition-shadow" data-testid="kpi-cash">
            <div className="flex items-center gap-2 text-blue-600 text-sm mb-3">
              <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                <DollarSign className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="font-medium">Cash Collected</span>
            </div>
            <p className="text-2xl font-heading font-bold text-blue-700 font-mono">
              {formatCurrency(data.cash?.total_inflows)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              {formatPercent(data.cash?.collection_rate)} collection rate
            </p>
          </div>

          {/* Accounts Receivable */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:shadow-card-hover transition-shadow" data-testid="kpi-receivables">
            <div className="flex items-center gap-2 text-amber-600 text-sm mb-3">
              <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                <Clock className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="font-medium">Outstanding A/R</span>
            </div>
            <p className="text-2xl font-heading font-bold text-amber-700 font-mono">
              {formatCurrency(data.receivables?.total_outstanding)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              {data.receivables?.overdue_count || 0} overdue
            </p>
          </div>

          {/* Inventory Value */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:shadow-card-hover transition-shadow" data-testid="kpi-inventory">
            <div className="flex items-center gap-2 text-purple-600 text-sm mb-3">
              <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center">
                <Package className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="font-medium">Inventory Value</span>
            </div>
            <p className="text-2xl font-heading font-bold text-purple-700 font-mono">
              {formatCurrency(data.inventory?.total_value)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              {data.inventory?.low_stock_count || 0} low stock alerts
            </p>
          </div>
        </div>

        {/* Profitability Section - Premium Design */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gross Profit */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6" data-testid="gross-profit-section">
            <h3 className="font-heading font-semibold text-slate-800 mb-5 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-primary-50 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-primary" strokeWidth={1.5} />
              </div>
              Gross Profit Analysis
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <span className="text-slate-600">Total Revenue</span>
                <span className="font-semibold font-mono text-slate-800">{formatCurrency(data.profitability?.total_revenue)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <span className="text-slate-600">Cost of Goods Sold (COGS)</span>
                <span className="font-semibold font-mono text-red-600">-{formatCurrency(data.profitability?.total_cogs)}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="font-heading font-semibold text-lg text-slate-800">Gross Profit</span>
                <span className="font-bold text-xl font-mono text-emerald-600">
                  {formatCurrency(data.profitability?.gross_profit)}
                </span>
              </div>
              <div className="bg-primary-50 rounded-xl p-4 text-center mt-4">
                <p className="text-xs text-primary font-medium mb-1">Gross Margin</p>
                <p className="text-3xl font-heading font-bold text-primary font-mono">
                  {formatPercent(data.profitability?.gross_margin)}
                </p>
              </div>
            </div>
          </div>

          {/* Cash Flow Summary */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6" data-testid="cash-flow-section">
            <h3 className="font-heading font-semibold text-slate-800 mb-5 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
              </div>
              Cash Flow Summary
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <span className="text-slate-600">Opening Balance</span>
                <span className="font-semibold font-mono text-slate-800">{formatCurrency(data.cash?.opening_balance)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <ArrowUpRight className="w-3 h-3 text-emerald-600" strokeWidth={2} />
                  </div>
                  <span className="text-slate-600">Cash In</span>
                </div>
                <span className="font-semibold font-mono text-emerald-600">+{formatCurrency(data.cash?.total_inflows)}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-red-50 flex items-center justify-center">
                    <ArrowDownRight className="w-3 h-3 text-red-600" strokeWidth={2} />
                  </div>
                  <span className="text-slate-600">Cash Out</span>
                </div>
                <span className="font-semibold font-mono text-red-600">-{formatCurrency(data.cash?.total_outflows)}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="font-heading font-semibold text-lg text-slate-800">Closing Balance</span>
                <span className="font-bold text-xl font-mono text-blue-600">
                  {formatCurrency(data.cash?.closing_balance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Breakdown Chart - Premium Design */}
        {data.sales?.by_source && data.sales.by_source.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6" data-testid="revenue-chart">
            <h3 className="font-heading font-semibold text-slate-800 mb-5">Revenue by Source</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.sales.by_source}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="source" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip 
                    formatter={(v) => formatCurrency(v)} 
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  />
                  <Bar dataKey="amount" fill="#059669" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Reconciliation Table - Premium Design */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6" data-testid="reconciliation-section">
          <h3 className="font-heading font-semibold text-slate-800 mb-5 flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary-50 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-primary" strokeWidth={1.5} />
            </div>
            Accounting Reconciliation
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Account</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Debit</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Credit</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                <tr className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 font-medium text-slate-800">Cash</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.reconciliation?.cash_debit)}</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.reconciliation?.cash_credit)}</td>
                  <td className="p-3 text-right font-mono text-sm font-semibold text-blue-600">{formatCurrency(data.cash?.closing_balance)}</td>
                </tr>
                <tr className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 font-medium text-slate-800">Accounts Receivable</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.reconciliation?.ar_debit)}</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.reconciliation?.ar_credit)}</td>
                  <td className="p-3 text-right font-mono text-sm font-semibold text-amber-600">{formatCurrency(data.receivables?.total_outstanding)}</td>
                </tr>
                <tr className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 font-medium text-slate-800">Inventory</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.reconciliation?.inventory_debit)}</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.reconciliation?.inventory_credit)}</td>
                  <td className="p-3 text-right font-mono text-sm font-semibold text-purple-600">{formatCurrency(data.inventory?.total_value)}</td>
                </tr>
                <tr className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 font-medium text-slate-800">Sales Revenue</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(0)}</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.sales?.total_revenue)}</td>
                  <td className="p-3 text-right font-mono text-sm font-semibold text-emerald-600">{formatCurrency(data.sales?.total_revenue)}</td>
                </tr>
                <tr className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 font-medium text-slate-800">Cost of Goods Sold</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(data.profitability?.total_cogs)}</td>
                  <td className="p-3 text-right font-mono text-sm">{formatCurrency(0)}</td>
                  <td className="p-3 text-right font-mono text-sm font-semibold text-red-600">{formatCurrency(data.profitability?.total_cogs)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Inventory Sales Report - Premium Design */}
        {data.inventory?.sales_report && data.inventory.sales_report.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6" data-testid="inventory-sales-section">
            <h3 className="font-heading font-semibold text-slate-800 mb-5 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center">
                <Package className="w-4 h-4 text-purple-600" strokeWidth={1.5} />
              </div>
              Inventory Sales Report
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Item</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Qty Sold</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Revenue</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">COGS</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Profit</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.inventory.sales_report.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-3 font-medium text-slate-800">{item.name}</td>
                      <td className="p-3 text-right font-mono text-sm">{item.quantity_sold}</td>
                      <td className="p-3 text-right font-mono text-sm">{formatCurrency(item.revenue)}</td>
                      <td className="p-3 text-right font-mono text-sm text-red-600">{formatCurrency(item.cogs)}</td>
                      <td className="p-3 text-right font-mono text-sm text-emerald-600">{formatCurrency(item.profit)}</td>
                      <td className="p-3 text-right font-mono text-sm font-semibold">{formatPercent(item.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Quick Links - Premium Design */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/dashboard/accounts-receivable" className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:border-primary hover:shadow-card-hover transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700 group-hover:text-primary">Accounts Receivable</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </div>
          </Link>
          <Link to="/dashboard/cash-flow/ledger" className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:border-primary hover:shadow-card-hover transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700 group-hover:text-primary">Cash Ledger</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </div>
          </Link>
          <Link to="/dashboard/inventory" className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:border-primary hover:shadow-card-hover transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700 group-hover:text-primary">Inventory</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </div>
          </Link>
          <Link to="/dashboard/cash-flow-statement" className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 hover:border-primary hover:shadow-card-hover transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700 group-hover:text-primary">Cash Flow Statement</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
